
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { AutomationConfigModel } from '@/models/automation_config';
import { SupplierModel } from '@/models/supplier';
import { StoreLocationModel } from '@/models/store_location';
import { StoreLocationList } from '@/collections/store_locations';
import { FilterColumnList } from '@/collections/filter_columns';
import { EventBus } from '@/main';
import SchedulePicker from './SchedulePicker.vue';
import AutomationFilterSection from './AutomationFilterSection.vue';

/**
 * A dialog form for creating and editing Automated PO Configurations.
 * It includes fields for naming, scheduling, filtering, and other settings.
 * @emits input - The visibility state of the dialog.
 * @emits save - The configuration object to be saved.
 */
@Component({
  components: {
    SchedulePicker,
    AutomationFilterSection,
  },
})
export default class AutomationConfigForm extends Vue {
  /**
   * Controls the visibility of the dialog. Passed via `v-model`.
   * @type {boolean}
   */
  @Prop({ type: Boolean, default: false }) value!: boolean;

  /**
   * The configuration object to be edited. If null, the form is in 'create' mode.
   * @type {AutomationConfigModel | null}
   */
  @Prop({ type: Object, default: null }) config!: AutomationConfigModel | null;

  /**
   * A list of available suppliers to populate the supplier dropdown.
   * @type {SupplierModel[]}
   */
  @Prop({ type: Array, default: () => [] }) suppliers!: SupplierModel[];

  /**
   * The store object, used for passing to child components like the filter section.
   * @type {any}
   */
  @Prop({ type: Object, default: null }) store!: any;

  /** Controls the form's validation state. */
  private valid = true;

  /** A local, editable copy of the configuration object. */
  private editableConfig: AutomationConfigModel = new AutomationConfigModel();
  
  /** A list of available store locations. */
  private locations: StoreLocationModel[] = [];

  /** Toggles the loading state of the locations dropdown. */
  private loadingLocations = false;

  /** Key to force re-render of the AutomationFilterSection component. */
  private filterKey = 0;

  /**
   * Validation rules for the form fields.
   */
  private rules = {
    required: (v: any) => !!v || 'This field is required',
    minValue: (v: number) => v === null || v === undefined || v >= 0 || 'Value must be non-negative',
    maxValue: (v: number) => {
      const min = this.editableConfig.minOrderValue;
      if (v === null || v === undefined) return true;
      if (min === null || min === undefined) return true;
      return v > min || 'Max value must be greater than min value';
    },
    emailList: (v: string[]) => {
      if (!v || v.length === 0) return 'At least one email is required';
      const emailRegex = /.+@.+\..+/;
      for (const email of v) {
        if (!emailRegex.test(email)) {
          return `Invalid email format: ${email}`;
        }
      }
      return true;
    },
    schedule: (v: AutomationConfigModel) => {
      const { scheduleType, scheduleConfig } = v;
      if (!scheduleType || !scheduleConfig) return 'Schedule is incomplete';

      switch (scheduleType) {
        case 'daily':
          if (scheduleConfig.hour === undefined) return 'Hour is required for daily schedule';
          break;
        case 'weekly':
          if (scheduleConfig.dayOfWeek === undefined) return 'Day of week is required for weekly schedule';
          if (scheduleConfig.hour === undefined) return 'Hour is required for weekly schedule';
          break;
        case 'monthly':
          if (scheduleConfig.dayOfMonth === undefined) return 'Day of month is required for monthly schedule';
          if (scheduleConfig.hour === undefined) return 'Hour is required for monthly schedule';
          break;
        default:
          return 'Invalid schedule type';
      }
      return true;
    },
  };

  /**
   * Computes the title of the form based on whether a config is being edited or created.
   * @returns {string} The form title.
   */
  get formTitle() {
    return this.config ? 'Edit Configuration' : 'New Configuration';
  }

  /**
   * Computed property to provide schedule data to SchedulePicker
   * @returns {object} The schedule data for the picker
   */
  get schedulePickerValue() {
    return {
      scheduleType: this.editableConfig.scheduleType,
      scheduleConfig: this.editableConfig.scheduleConfig,
    };
  }

  /**
   * Computed property for delivery location ID to ensure proper v-select binding
   */
  get deliveryLocationIdComputed() {
    return this.editableConfig.deliveryLocationId;
  }

  set deliveryLocationIdComputed(value: number | null) {
    this.editableConfig.deliveryLocationId = value;
  }

  /**
   * Resets the form to its initial state when the dialog is opened.
   * This ensures that when creating a new config, the form is not pre-filled
   * with data from a previously edited config.
   */
  @Watch('value')
  async onDialogStateChange(isOpen: boolean) {
    if (isOpen) {
      // First fetch locations to ensure dropdowns work properly
      await this.fetchLocations();
      
      if (this.config) {
        // Editing an existing config
        this.editableConfig = new AutomationConfigModel(this.config.toJSON());
        
        // Ensure filterColumns is properly initialized
        if (this.config.filterColumns && this.config.filterColumns.items && this.config.filterColumns.items.length > 0) {
          // The AutomationConfigModel constructor should handle this, but let's make sure
          // Force update the filter key to trigger re-render of AutomationFilterSection
          this.filterKey = Math.random();
        }
        
        // Log the current state
        
        // Ensure supplierId is properly set and matches supplier list
        if (this.editableConfig.supplierId) {
          const supplierId = Number(this.editableConfig.supplierId);
          const supplierExists = this.suppliers.some(sup => sup.id === supplierId);
          
          if (supplierExists) {
            this.editableConfig.supplierId = supplierId;
          } else {
            console.warn(`Supplier ID ${supplierId} not found in available suppliers`);
          }
        }
        
        // Ensure deliveryLocationId is a number and exists in locations
        if (this.editableConfig.deliveryLocationId) {
          const deliveryId = Number(this.editableConfig.deliveryLocationId);
          const locationExists = this.locations.some(loc => loc.id === deliveryId);
          
          if (locationExists) {
            this.editableConfig.deliveryLocationId = deliveryId;
          } else {
            console.warn(`Delivery location ID ${deliveryId} not found in available locations`);
            this.editableConfig.deliveryLocationId = null;
          }
        }
        
        // Ensure locationIds are numbers and exist in locations
        if (this.editableConfig.locationIds && this.editableConfig.locationIds.length > 0) {
          this.editableConfig.locationIds = this.editableConfig.locationIds
            .map(id => Number(id))
            .filter(id => this.locations.some(loc => loc.id === id));
        }
        
      } else {
        // Creating a new config
        this.resetForm();
      }
      
      this.$nextTick(() => {
        (this.$refs.form as any)?.resetValidation();
      });
    }
  }

  /**
   * Initializes the component by setting up a default editableConfig.
   * This is especially important for the 'create' mode.
   */
  created() {
    this.resetForm();
  }

  /**
   * Closes the dialog without saving.
   */
  close() {
    this.$emit('input', false);
  }

  /**
   * Validates the form and emits the 'save' event with the configuration data.
   */
  async save() {
    if ((this.$refs.form as any).validate()) {
      this.$emit('save', this.editableConfig);
      this.close();
    }
  }

  /**
   * Fetches the list of store locations from the API.
   */
  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      const locationList = new StoreLocationList();
      await locationList.fetch();
      this.locations = locationList.items;
      
      // Ensure all location IDs are numbers
      this.locations.forEach(location => {
        if (location.id) {
          location.id = Number(location.id);
        }
      });
      
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      EventBus.$emit('show-error', 'Could not load locations.');
    } finally {
      this.loadingLocations = false;
    }
  }

  /**
   * Resets the editableConfig to its default state for creating a new configuration.
   */
  private resetForm() {
    this.editableConfig = new AutomationConfigModel({
      name: '',
      merchantId: this.store?.merchant?.id || this.store?.merchantId,
      supplierId: null,
      locationIds: [],
      deliveryLocationId: null,
      scheduleType: 'daily',
      scheduleConfig: {
        hour: 9, // Default to 9 AM
      },
      filterColumns: [], // This will be converted to FilterColumnList by the model
      productSegmentId: null,
      minOrderValue: null,
      maxOrderValue: null,
      emailRecipients: this.store?.user?.email ? [this.store.user.email] : [],
      isActive: true,
    });
    this.filterKey = Math.random(); // Force re-render
  }

  /**
   * Updates the schedule properties while preserving the AutomationConfigModel instance
   * @param {object} scheduleData - The schedule data from SchedulePicker
   */
  private updateSchedule(scheduleData: any) {
    // Update only the schedule-related properties to preserve the model instance
    if (scheduleData.scheduleType !== undefined) {
      this.editableConfig.scheduleType = scheduleData.scheduleType;
    }
    if (scheduleData.scheduleConfig !== undefined) {
      this.editableConfig.scheduleConfig = scheduleData.scheduleConfig;
    }
  }

  /**
   * Updates the filter columns while preserving the AutomationConfigModel instance
   * @param {FilterColumnList} newFilterColumns - The new filter columns from AutomationFilterSection
   */
  private updateFilterColumns(newFilterColumns: FilterColumnList) {
    this.editableConfig.filterColumns = newFilterColumns;
  }
}
